import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { ERRORS } from 'src/app/components/reports/reports.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  GroupErrorReportRequest,
  GroupErrorReports,
} from 'src/app/core/models/reports/error-reports.model';
import { ErrorReportsService } from 'src/app/core/services/reports/error/error-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  FILE_NAME,
  GROUP_ERROR_CONSTS,
  GROUP_ERROR_PDF_REPORT,
} from './group-error-report.constants';

@Component({
  selector: 'app-group-error-report',
  standalone: false,
  templateUrl: './group-error-report.component.html',
  styleUrl: './group-error-report.component.scss',
})
export class GroupErrorReportComponent implements OnInit {
  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  groupErrorReportForm!: FormGroup;
  groupErrorReportResponse!: GroupErrorReports;

  currDate!: Date;
  minDate!: Date;
  customMaxDate!: Date;
  toDate!: Date;

  readonly BUTTON_TYPES = BUTTON_TYPES;
  readonly CONSTS = GROUP_ERROR_CONSTS;
  readonly PDF_REPORT = GROUP_ERROR_PDF_REPORT;

  private readonly commonUtils = inject(CommonUtils);
  private readonly reportUtils = inject(ReportsUtils);
  private readonly errorReportsService = inject(ErrorReportsService);
  private readonly userService = inject(UserService);
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef: MatDialogRef<GroupErrorReportComponent> = inject(
    MatDialogRef<GroupErrorReportComponent>,
  );

  ngOnInit() {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 7,
    );
    this.customMaxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate(),
    );
    this.toDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate(),
    );

    this.groupErrorReportForm = this.fb.nonNullable.group({
      fromDate: [this.toDate],
      toDate: [this.toDate],
      reportFormat: [Options.PDF],
    });
  }
  /**
   * Used to get Group Error Report
   *
   * @private
   * @param {string} locationCode
   * @param {string} groupCode
   * @memberof ReportsComponent
   */
  getGroupErrorReport() {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const formValue = this.groupErrorReportForm.value;
          const locationCode = user?.location;
          const groupCode = user?.locationGroup;
          const fromDate = this.commonUtils.getFormattedDate(
            formValue.fromDate,
          );
          const toDate = this.commonUtils.getFormattedDate(formValue.toDate);

          const request: GroupErrorReportRequest = {
            locationCode: locationCode || '',
            groupCode: groupCode || '',
            fromDate: fromDate,
            toDate: toDate,
          };
          return this.errorReportsService.getGroupErrorReport(request).pipe(
            take(1),
            map((data) => {
              const formatType =
                this.groupErrorReportForm.get('reportFormat')?.value;

              const groupError = data?.response?.groupError || [];
              this.groupErrorReportResponse = data;

              if (groupError.length) {
                if (formatType === Options.CSV) {
                  const customHeader = [
                    [
                      '',
                      '',
                      '',
                      '',
                      `${ERRORS.buttons[1].text.toUpperCase()}`,
                      '',
                      '',
                      '',
                      '',
                    ],
                  ];
                  this.reportUtils.exportCSVReport(
                    groupError,
                    ERRORS.buttons[1].text,
                    customHeader,
                  );
                } else {
                  this.groupErrorReportResponse = data;
                  setTimeout(() => {
                    const content =
                      this.pdfContainer.element.nativeElement.innerHTML;
                    this.reportUtils.exportPdfReport(
                      content,
                      GROUP_ERROR_PDF_REPORT.NAME,
                      FILE_NAME,
                    );
                  }, 0);
                }
                return this.dialogRef.close();
              } else {
                this.commonUtils.updateBannerType(
                  BANNER_TYPE.NO_DATA_AVAILABLE,
                );
                return this.dialogRef.close();
              }
            }),
          );
        }),
      )
      .subscribe();
  }

  radioChange(newValue: Options) {
    this.groupErrorReportForm.get('reportFormat')?.setValue(newValue);
  }

  clickExit() {
    this.commonUtils.openExitModal(this.groupErrorReportForm.dirty);
  }
}
